import React from 'react';
import { DataGrid, Button } from '@privemind/components';
import { BucketSummaryCard } from '../components/bucket-summary-card/bucket-summary-card';
import { CreateBucketCard } from '../components/create-bucket-card/create-bucket-card';
import { EditAssetModal } from '../components/edit-asset-modal/edit-asset-modal';
import { DeleteAssetModal } from '../components/delete-asset-modal/delete-asset-modal';
import { AssetDisplayData, BucketDisplayData, Asset } from '../types';
import './edit-assets-ui.scss';

export interface EditAssetsUIProps {
  // Data props
  bucketDisplayData: BucketDisplayData[];
  totalAssets: number;
  assetDisplayData: AssetDisplayData[];
  selectedAsset: AssetDisplayData | null;
  selectedAssetData: Asset | null;
  
  // State props
  editModalOpen: boolean;
  deleteModalOpen: boolean;
  
  // Callback props
  onViewBucket: (bucketName: string) => void;
  onDeleteBucket: (bucketName: string) => void;
  onEditAsset: (asset: AssetDisplayData) => void;
  onDeleteAsset: (asset: AssetDisplayData) => void;
  onCloseEditModal: () => void;
  onCloseDeleteModal: () => void;
}

export const EditAssetsUI: React.FC<EditAssetsUIProps> = ({
  bucketDisplayData,
  totalAssets,
  assetDisplayData,
  selectedAsset,
  selectedAssetData,
  editModalOpen,
  deleteModalOpen,
  onViewBucket,
  onDeleteBucket,
  onEditAsset,
  onDeleteAsset,
  onCloseEditModal,
  onCloseDeleteModal,
}) => {
  return (
    <div className="edit-assets">
      <div className="edit-assets__buckets">
        <h2>Your Buckets</h2>
        <div className="edit-assets__bucket-grid">
          <CreateBucketCard />
          {bucketDisplayData.map((bucket) => (
            <BucketSummaryCard
              key={bucket.name}
              bucket={bucket}
              onViewBucket={onViewBucket}
              onDeleteBucket={onDeleteBucket}
            />
          ))}
        </div>
        {bucketDisplayData.length === 0 && (
          <div className="edit-assets__empty-message">
            <p>Create your first bucket using the card above!</p>
          </div>
        )}
      </div>

      {totalAssets > 0 && (
        <div className="edit-assets__all-assets">
          <h2>All Assets</h2>
          <DataGrid
            rows={assetDisplayData.slice(0, 5)}
            columns={[
              { 
                id: 'symbol',
                header: 'Asset',
                render: (row: unknown) => (row as AssetDisplayData).symbol
              },
              { 
                id: 'bucket',
                header: 'Bucket',
                render: (row: unknown) => (row as AssetDisplayData).bucket
              },
              { 
                id: 'amount',
                header: 'Quantity',
                render: (row: unknown) => (row as AssetDisplayData).amount.toFixed(8)
              },
              { 
                id: 'currentPrice',
                header: 'Price',
                render: (row: unknown) => `$${(row as AssetDisplayData).currentPrice.toFixed(2)}`
              },
              { 
                id: 'marketValue',
                header: 'Value',
                render: (row: unknown) => `$${(row as AssetDisplayData).marketValue.toFixed(2)}`
              },
              { 
                id: 'pnl',
                header: 'P&L',
                render: (row: unknown) => {
                  const asset = row as AssetDisplayData;
                  const pnl = asset.pnl || 0;
                  return (
                    <span className={pnl >= 0 ? 'positive' : 'negative'}>
                      ${pnl.toFixed(2)}
                    </span>
                  );
                }
              },
              { 
                id: 'actions',
                header: 'Actions',
                render: (row: unknown) => {
                  const asset = row as AssetDisplayData;
                  return (
                    <div className="action-buttons">
                      <Button
                        color="secondary"
                        onClick={() => onEditAsset(asset)}
                      >
                        Edit
                      </Button>
                      <Button
                        color="danger"
                        onClick={() => onDeleteAsset(asset)}
                      >
                        Delete
                      </Button>
                    </div>
                  );
                }
              }
            ]}
          />
        </div>
      )}

      {selectedAsset && selectedAssetData && (
        <>
          <EditAssetModal
            asset={selectedAssetData}
            assetId={`${selectedAsset.id}_${selectedAsset.bucket}`}
            isOpen={editModalOpen}
            onClose={onCloseEditModal}
          />
          <DeleteAssetModal
            asset={selectedAssetData}
            assetId={`${selectedAsset.id}_${selectedAsset.bucket}`}
            isOpen={deleteModalOpen}
            onClose={onCloseDeleteModal}
          />
        </>
      )}
    </div>
  );
};
